import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * FormButton
 * -----------
 * Centralizes the branded buttons that were previously duplicated as inline
 * className constants across the enrollment flow (nextButton / continueButton /
 * previousButton and the "Get Started" CTA on FinancialMeeting, FinancialMpn,
 * and BookingsPanel).
 *
 * Design:
 *   - `base` holds ONLY what every branded button shares (layout + focus).
 *   - `intent` controls color/role and pulls from the design tokens defined in
 *     consolidated-global.css (bg-ecpi-blue, --ecpi-yellow-hex) rather than
 *     hard-coded hex values. Each intent also carries its mouse-over and mouse-down
 *     look (`enabled:` so a disabled or saving button does not react), so a click
 *     shows at once instead of only when the next page loads (UAT SEP-069).
 *   - `shape` controls sizing/geometry, so the compact wizard-nav buttons and
 *     the large "Get Started" CTA can coexist without a forced common size.
 *   - `loading` shows a spinner, marks the button busy and ignores further clicks
 *     (it is disabled, but keeps its full color so it does not look unavailable).
 *
 * `formButtonVariants` is exported separately so non-button elements (e.g. the
 * external-link <a> on FinancialMpn) can reuse the exact same styling.
 */
export const formButtonVariants = cva(
    "inline-flex items-center justify-center font-medium transition-[background-color,border-color,box-shadow,scale] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ecpi-blue/50 disabled:opacity-50 disabled:cursor-not-allowed enabled:cursor-pointer enabled:active:scale-[0.98]",
    {
        variants: {
            intent: {
                // Primary brand blue (former nextButton and "Get Started" CTA)
                primary: "bg-ecpi-blue text-white enabled:hover:bg-[#0a3d84] enabled:hover:shadow-md enabled:active:bg-[#08306b] enabled:active:shadow-inner",
                // Gold accent (former continueButton) -- token, not #FFC009
                accent: "bg-[var(--ecpi-yellow-hex)] text-[#292E33] font-sora font-semibold enabled:hover:bg-[#E6AC00] enabled:hover:shadow-md enabled:active:bg-[#C99700] enabled:active:shadow-inner",
                // Neutral white (the app-wide "Save & Exit" button)
                neutral: "bg-white text-black enabled:hover:bg-[#E8EEF6] enabled:hover:shadow-[inset_0_0_0_1px_#8FAACB] enabled:active:bg-[#DCE5F1] enabled:active:shadow-[inset_0_0_0_1px_#8FAACB,inset_0_2px_4px_rgba(0,0,0,0.08)]",
                // Sign-in and registration pages: the yellow gradient primary and the pale-blue secondary (were .btn-primary-ov /
                // .btn-secondary-ov and the same colours repeated inline)
                authPrimary: "text-black font-semibold bg-[linear-gradient(to_right,#FFBD09,#FFF600)] enabled:hover:bg-[linear-gradient(to_right,#E5AA08,#E5DD00)]",
                authSecondary: "bg-[#E1EAF4] text-[#030512] font-normal enabled:hover:bg-[#AFC3D9] enabled:hover:underline",
                // Soft red (the Remove button on repeated entries)
                danger: "bg-red-100 text-red-700 enabled:hover:bg-red-200 enabled:active:bg-red-300",
                // Neutral white with slate border(the app-wide "Previous" button)
                neutralBorder: "bg-white text-black text-[#3D444D] font-sora font-semibold border border-[#C0D2E6] enabled:hover:bg-[#E8EEF6] enabled:hover:border-[#8FAACB] enabled:active:bg-[#DCE5F1] enabled:active:shadow-inner",

            },
            shape: {
                // Compact wizard navigation buttons
                nav: "gap-2 px-4 py-2 rounded-lg",
                // Sign-in and registration pages: full-width, 50px tall
                auth: "w-full min-h-[50px] px-4 py-3 rounded-md text-center",
                // Large call-to-action ("Get Started")
                cta: "gap-2 w-80 h-13 px-4 py-2 text-lg rounded-sm",
            },
        },
        defaultVariants: {
            intent: "primary",
            shape: "nav",
        },
    }
);

export type FormButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
    VariantProps<typeof formButtonVariants> & {
        /** A save or request is running: shows a spinner, marks the button busy and ignores clicks. */
        loading?: boolean;
    };

export const FormButton = React.forwardRef<HTMLButtonElement, FormButtonProps>(
    ({ className, intent, shape, type = "button", loading = false, disabled, children, ...props }, ref) => (
        <button
            ref={ref}
            type={type}
            disabled={disabled || loading}
            aria-busy={loading || undefined}
            className={cn(formButtonVariants({ intent, shape }), loading && "disabled:opacity-90 disabled:cursor-progress", className)}
            {...props}
        >
            {loading && (
                <span
                    className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent"
                    aria-hidden="true"
                />
            )}
            {children}
        </button>
    )
);

FormButton.displayName = "FormButton";
